import React, { Dispatch, FC, useState, KeyboardEvent } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { connect } from 'react-redux';

import { uploadImageToBackend } from './helpers/uploadImage';
import handleKeyPress from '../../../../../common/handleKeyPress';
import resizeImage from '../../../../../common/resizeImage';
import * as Styled from './styles';
import BananatagButton from '../../../../../componentLibrary/BananatagButton';
import eventService from '../../../../../common/eventService';
import rules from '../../../../../constants/businessRules/businessRules';
import { ERR_UPLOAD_CONTENT } from '../../../../../constants/Errors';
import { TAppState } from '../../../../../reducers';
import UploadingImage from '../../../../../componentLibrary/UploadingImage';

interface IProps {
    selectedElementData: any;
    hideImageLibrary: () => void;
    IsRawImageUploadEnabled: boolean;
    dispatch: Dispatch<any>;
}

const UploadImageTab: FC<IProps> = ({ selectedElementData, hideImageLibrary, IsRawImageUploadEnabled, dispatch }) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [activeImage, setActiveImage] = useState<Blob | null>();
    const [itemDropped, setItemDropped] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [fileName, setFileName] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    const uploadImageToDraft = async () => {
        if (!activeImage) {
            eventService.notify(ERR_UPLOAD_CONTENT, true);
            return;
        }
        const reader = new FileReader();
        setIsUploading(true);
        reader.readAsDataURL(activeImage);
        reader.onload = async () => {
            let fileExtension: string;
            const regex = /^image\/(png|jpg|jpeg|gif)/;
            const matchResults = activeImage.type.match(regex);
            if (matchResults) {
                // eslint-disable-next-line prefer-destructuring
                fileExtension = matchResults[1];
            } else {
                eventService.notify('No file Extension found, please consider uploading another image.', true);
                return;
            }
            const isGif = fileExtension === 'gif';
            try {
                if (isGif) {
                    await uploadImageToBackend(reader.result, selectedElementData, fileExtension, dispatch, false);
                    hideImageLibrary();
                    return;
                }
                const imageData = IsRawImageUploadEnabled
                    ? (reader.result as string)
                    : await resizeImage(reader.result as string, activeImage.type);

                await uploadImageToBackend(imageData, selectedElementData, fileExtension, dispatch, false);
                setIsUploading(false);
                hideImageLibrary();
            } catch (err) {
                eventService.notify(err, true);
                setIsUploading(false);
            }
        };
    };

    const handleUpload = (imageFile: File) => {
        const isValidFile = imageFile.type.includes('image/');
        setErrorMsg('');
        setFileName(imageFile.name);
        setItemDropped(true);
        if (!isValidFile) {
            setErrorMsg('Unsupported file format');
            return;
        }
        const isGif = imageFile.type === 'image/gif';
        const sizeRule = isGif ? rules.MAX_GIF_UPLOAD_FILE_SIZE : rules.MAX_IMAGE_UPLOAD_FILE_SIZE;
        const isValidSize = imageFile.size / 1024 / 1024 <= sizeRule;
        if (!isValidSize) {
            setErrorMsg(`File is too big (> ${sizeRule} MB)`);
        } else {
            setActiveImage(imageFile);
            setPreviewUrl(URL.createObjectURL(imageFile));
        }
    };

    const onDeleteImageClick = () => {
        setItemDropped(false);
        setActiveImage(null);
        setPreviewUrl('');
    };

    return (
        <div>
            {itemDropped && !errorMsg && !isUploading ? (
                <Styled.PreviewDiv>
                    {previewUrl && (
                        <div style={{ position: 'relative' }}>
                            <Styled.ImageUploadPreview src={previewUrl} alt="preview" />
                            <Styled.DeleteImage
                                icon="times-circle"
                                onClick={() => onDeleteImageClick()}
                                onKeyDown={(e) =>
                                    handleKeyPress(e as unknown as KeyboardEvent<HTMLDivElement>, () =>
                                        onDeleteImageClick()
                                    )
                                }
                                role="button"
                                aria-hidden="false"
                                aria-label="Delete image"
                                tabIndex={0}
                            />
                        </div>
                    )}
                </Styled.PreviewDiv>
            ) : (
                <Styled.DropZoneWrapper>
                    <Styled.DropZoneText>
                        This image will be added to my Draft only (NOT the Shared Library).
                    </Styled.DropZoneText>
                    <UploadingImage
                        isUploading={isUploading}
                        handleUpload={handleUpload}
                        fileName={fileName}
                        errorMessage={errorMsg}
                        showUploader
                    />
                </Styled.DropZoneWrapper>
            )}
            <Styled.ModalButtons>
                <BananatagButton
                    size="medium"
                    text="SELECT
                    IMAGE"
                    disabled={!previewUrl}
                    type="solid"
                    onClick={() => uploadImageToDraft()}
                />
            </Styled.ModalButtons>
        </div>
    );
};

UploadImageTab.propTypes = {
    dispatch: PropTypes.func.isRequired,
    selectedElementData: PropTypes.object.isRequired,
    hideImageLibrary: PropTypes.func.isRequired,
    IsRawImageUploadEnabled: PropTypes.bool.isRequired,
};

const mapStateToProps = (state: TAppState) => ({
    IsRawImageUploadEnabled: _get(state, 'session.user.features.EMAIL_DESIGNER_RAW_IMAGE_UPLOA', false),
});

export default connect(mapStateToProps)(UploadImageTab);
